/**
 * Diagnostic utility for debugging pointer events issues
 * Helps identify and fix sidebar unclickable bugs after modal closes
 */

interface PointerEventsDiagnostic {
  bodyPointerEvents: string;
  bodyOverflow: string;
  bodyClasses: string[];
  stuckOverlays: Element[];
  blockingElements: Array<{
    element: HTMLElement;
    tagName: string;
    className: string;
    zIndex: string;
    pointerEvents: string;
    position: string;
  }>;
  elementsAtSidebarPosition: Element[];
}

/**
 * Run comprehensive diagnostic on pointer events state
 */
export function diagnosePointerEvents(): PointerEventsDiagnostic {
  const diagnostic: PointerEventsDiagnostic = {
    bodyPointerEvents: document.body.style.pointerEvents,
    bodyOverflow: document.body.style.overflow,
    bodyClasses: Array.from(document.body.classList),
    stuckOverlays: [],
    blockingElements: [],
    elementsAtSidebarPosition: [],
  };

  // Check for stuck Radix dialog overlays
  const overlays = document.querySelectorAll("[data-radix-dialog-overlay]");
  overlays.forEach((overlay) => {
    const state = overlay.getAttribute("data-state");
    if (state === "closed" || !state) {
      diagnostic.stuckOverlays.push(overlay);
    }
  });

  // Check for blocking elements with high z-index
  const allElements = document.querySelectorAll("*");
  allElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    const computed = window.getComputedStyle(htmlEl);
    const zIndex = parseInt(computed.zIndex || "0");

    if (
      computed.position === "fixed" &&
      zIndex > 40 &&
      (computed.pointerEvents === "none" || computed.opacity === "0")
    ) {
      diagnostic.blockingElements.push({
        element: htmlEl,
        tagName: htmlEl.tagName,
        className: htmlEl.className,
        zIndex: computed.zIndex,
        pointerEvents: computed.pointerEvents,
        position: computed.position,
      });
    }
  });

  // Check what elements are at typical sidebar positions
  const sidebarPositions = [
    { x: 50, y: 100 },
    { x: 100, y: 200 },
    { x: 150, y: 300 },
    { x: 200, y: 400 },
  ];

  sidebarPositions.forEach((pos) => {
    const elements = document.elementsFromPoint(pos.x, pos.y);
    diagnostic.elementsAtSidebarPosition.push(...elements);
  });

  return diagnostic;
}

/**
 * Log diagnostic information to console
 */
export function logPointerEventsDiagnostic(): void {
  const diagnostic = diagnosePointerEvents();

  console.group("🔍 Pointer Events Diagnostic");

  console.log("📋 Document Body State:", {
    pointerEvents: diagnostic.bodyPointerEvents || "(not set)",
    overflow: diagnostic.bodyOverflow || "(not set)",
    classes: diagnostic.bodyClasses,
  });

  if (diagnostic.stuckOverlays.length > 0) {
    console.warn("⚠️ Stuck Modal Overlays Found:", diagnostic.stuckOverlays);
  } else {
    console.log("✅ No stuck modal overlays");
  }

  if (diagnostic.blockingElements.length > 0) {
    console.warn(
      "⚠️ Potentially Blocking Elements:",
      diagnostic.blockingElements,
    );
  } else {
    console.log("✅ No obvious blocking elements");
  }

  console.log(
    "📍 Elements at sidebar positions (first 3):",
    diagnostic.elementsAtSidebarPosition
      .slice(0, 3)
      .map(
        (el) =>
          `${el.tagName}${el.className ? "." + el.className.split(" ").join(".") : ""}`,
      ),
  );

  console.groupEnd();
}

/**
 * Attempt to fix pointer events issues
 */
export function fixPointerEvents(): boolean {
  console.log("🔧 Attempting to fix pointer events...");

  let fixed = false;

  // Fix body pointer events
  if (document.body.style.pointerEvents === "none") {
    document.body.style.pointerEvents = "";
    console.log("✅ Fixed body pointer-events: none");
    fixed = true;
  }

  // Fix body overflow
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
    console.log("✅ Fixed body overflow: hidden");
    fixed = true;
  }

  // Remove stuck overlays
  const stuckOverlays = document.querySelectorAll(
    '[data-radix-dialog-overlay][data-state="closed"]',
  );
  if (stuckOverlays.length > 0) {
    stuckOverlays.forEach((overlay) => overlay.remove());
    console.log(`✅ Removed ${stuckOverlays.length} stuck overlay(s)`);
    fixed = true;
  }

  // Check for invisible blocking elements and fix them
  const diagnostic = diagnosePointerEvents();
  diagnostic.blockingElements.forEach((blockingEl) => {
    if (blockingEl.pointerEvents === "none") {
      blockingEl.element.style.pointerEvents = "auto";
      console.log(
        `✅ Fixed pointer-events on ${blockingEl.tagName}.${blockingEl.className}`,
      );
      fixed = true;
    }
  });

  if (!fixed) {
    console.log("ℹ️ No pointer events issues detected");
  }

  return fixed;
}

// Extend Window interface for diagnostic tools
declare global {
  interface Window {
    pointerEventsDiagnostic?: {
      diagnose: () => PointerEventsDiagnostic;
      log: () => void;
      fix: () => boolean;
    };
  }
}

/**
 * Add diagnostic tools to window for browser console debugging
 */
export function addDiagnosticTools(): void {
  if (typeof window !== "undefined") {
    window.pointerEventsDiagnostic = {
      diagnose: diagnosePointerEvents,
      log: logPointerEventsDiagnostic,
      fix: fixPointerEvents,
    };

    console.log(
      "🛠️ Pointer events diagnostic tools added to window.pointerEventsDiagnostic",
    );
    console.log("Available commands:");
    console.log("  window.pointerEventsDiagnostic.log() - Log current state");
    console.log(
      "  window.pointerEventsDiagnostic.fix() - Attempt to fix issues",
    );
    console.log(
      "  window.pointerEventsDiagnostic.diagnose() - Get raw diagnostic data",
    );
  }
}

/**
 * Monitor pointer events and automatically log issues
 */
export function monitorPointerEvents(intervalMs: number = 5000): () => void {
  const interval = setInterval(() => {
    const diagnostic = diagnosePointerEvents();

    if (
      diagnostic.bodyPointerEvents === "none" ||
      diagnostic.stuckOverlays.length > 0 ||
      diagnostic.blockingElements.length > 0
    ) {
      console.warn("🚨 Pointer events issue detected!");
      logPointerEventsDiagnostic();
    }
  }, intervalMs);

  return () => clearInterval(interval);
}

/**
 * Emergency fix function that can be called from browser console
 */
export function emergencyFix(): void {
  console.log("🚨 Running emergency pointer events fix...");

  // Aggressive cleanup
  document.body.style.pointerEvents = "";
  document.body.style.overflow = "";

  // Remove all Radix overlays
  const allOverlays = document.querySelectorAll("[data-radix-dialog-overlay]");
  allOverlays.forEach((overlay) => overlay.remove());

  // Remove any fixed position elements with high z-index that might be blocking
  const highZElements = Array.from(document.querySelectorAll("*")).filter(
    (el) => {
      const computed = window.getComputedStyle(el as HTMLElement);
      const zIndex = parseInt(computed.zIndex || "0");
      return computed.position === "fixed" && zIndex > 50;
    },
  );

  highZElements.forEach((el) => {
    const computed = window.getComputedStyle(el as HTMLElement);
    if (computed.pointerEvents === "none" || computed.opacity === "0") {
      (el as HTMLElement).style.display = "none";
      console.log(`🗑️ Hid potentially blocking element: ${el.tagName}`);
    }
  });

  console.log("✅ Emergency fix completed");

  // Run diagnostic after fix
  setTimeout(() => {
    logPointerEventsDiagnostic();
  }, 100);
}
